import React, { useState, useRef, useEffect } from 'react';
import { SimulationTemplate, SimulationStep, ResultBlock } from '../SimulationTemplate';
import { Text } from '../../../components/ui/typography/Text';
import { OutlineButton } from '../../../components/ui/buttons/OutlineButton';
import { AccentButton } from '../../../components/ui/buttons/AccentButton';
import { Brain, Microscope, BookOpen } from 'lucide-react';

const StatusQuoBiasSimulation: React.FC = () => {
  const [stage, setStage] = useState<'intro' | 'scenario' | 'results'>('intro');
  const [currentFrame, setCurrentFrame] = useState(0);
  const [choices, setChoices] = useState<{ stayFrame: string | null, switchFrame: string | null }>({ 
    stayFrame: null, 
    switchFrame: null 
  });
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [currentChoice, setCurrentChoice] = useState<string | null>(null);
  
  const simulationRef = useRef<HTMLDivElement>(null);
  
  // Reset currentChoice when currentFrame changes
  useEffect(() => {
    setCurrentChoice(null);
  }, [currentFrame]);
  
  // Study data based on Samuelson & Zeckhauser (1988)
  const studyResults = {
    stayFrame: {
      stayedWithCurrent: 60,
      switchedToNew: 40
    },
    switchFrame: {
      stayedWithCurrent: 39,
      switchedToNew: 61
    }
  };
  
  // Scenario configurations from the original study
  const frames = [
    {
      id: 'stayFrame',
      title: "Scenario 1: Current Investment",
      description: "You have inherited a large sum of money from your great uncle. A significant portion of this portfolio is invested in a moderate-risk company (Company A). You are now faced with the choice of what to do with this money. You have the following options:",
      options: [
        "Keep the money invested in Company A, with a moderate-risk, moderate-return profile.",
        "Transfer the money into Company B, with a moderate-risk, moderate-return profile but a different industry focus."
      ]
    },
    {
      id: 'switchFrame',
      title: "Scenario 2: New Investment",
      description: "You have inherited a large sum of money from your great uncle. You are now faced with the choice of what to do with this money. You have the following options:",
      options: [
        "Invest the money in Company A, with a moderate-risk, moderate-return profile.",
        "Invest the money in Company B, with a moderate-risk, moderate-return profile but a different industry focus."
      ]
    }
  ];
  
  // Start the simulation
  const handleStart = () => {
    setStage('scenario');
  };
  
  // Handle choice selection
  const handleChoice = (selectedOption: string) => {
    if (isTransitioning) return; // Prevent multiple clicks during transition
    
    // First set the current selection for immediate visual feedback
    setCurrentChoice(selectedOption);
    setIsTransitioning(true);
    
    const frameId = frames[currentFrame].id as 'stayFrame' | 'switchFrame';
    
    // Update choices for the current frame
    setChoices({
      ...choices,
      [frameId]: selectedOption
    });
    
    // Move to next frame or results with a smoother transition
    if (currentFrame < frames.length - 1) {
      // Use a longer delay to make selection visible
      setTimeout(() => {
        setCurrentFrame(prevFrame => prevFrame + 1);
        // currentChoice will be reset by the useEffect when currentFrame changes
        setIsTransitioning(false);
      }, 800); // Longer delay for visibility of selection
    } else {
      setTimeout(() => {
        setStage('results');
        setIsTransitioning(false);
      }, 800); // Consistent delay for last frame too
    }
  };
  
  // Reset the simulation
  const handleReset = () => {
    setCurrentFrame(0);
    setChoices({ stayFrame: null, switchFrame: null });
    setCurrentChoice(null);
    setIsTransitioning(false);
    setStage('scenario');
    simulationRef.current?.scrollIntoView({ behavior: 'smooth' });
  };
  
  // Component for choice buttons
  const ChoiceButtons = ({ frameIndex }: { frameIndex: number }) => {
    const handleSelect = (choice: string) => {
      if (isTransitioning) return;
      handleChoice(choice);
    };
    
    return (
      <div className="space-y-2 sm:space-y-3 my-2 sm:my-3">
        <OutlineButton 
          onClick={() => handleSelect('A')}
          className={`w-full text-left justify-start p-2.5 sm:p-3 ${currentChoice === 'A' ? 'border-2 border-orange-500 bg-orange-50' : ''}`}
          active={currentChoice === 'A'}
          disabled={isTransitioning}
        >
          {frames[frameIndex].options[0]}
        </OutlineButton>
        
        <OutlineButton 
          onClick={() => handleSelect('B')}
          className={`w-full text-left justify-start p-2.5 sm:p-3 ${currentChoice === 'B' ? 'border-2 border-orange-500 bg-orange-50' : ''}`}
          active={currentChoice === 'B'}
          disabled={isTransitioning}
        >
          {frames[frameIndex].options[1]}
        </OutlineButton>
      </div>
    );
  };

  return (
    <SimulationTemplate
      title="Status Quo Bias Simulation"
      description="Explore how framing affects decision-making, based on Samuelson and Zeckhauser's classic 1988 study. This simulation demonstrates how the presentation of options as either status quo or new alternatives can significantly influence our choices."
    >
      {stage === 'intro' && (
        <div className="flex justify-center">
          <AccentButton onClick={handleStart}>
            Start Simulation
          </AccentButton>
        </div>
      )}

      {stage !== 'intro' && (
        <div ref={simulationRef} className="space-y-6 sm:space-y-8">
          {stage === 'scenario' && (
            <SimulationStep title={frames[currentFrame].title}>
              <Text className="mb-3 sm:mb-4 text-gray-700">
                {frames[currentFrame].description}
              </Text>
              
              <ChoiceButtons frameIndex={currentFrame} />
              
              <Text size="sm" className="mt-2 sm:mt-3 text-gray-500">
                Scenario {currentFrame + 1} of {frames.length}
              </Text>
            </SimulationStep>
          )}

          {stage === 'results' && (
            <>
              <SimulationStep title="Your Results">
                <div className="space-y-4">
                  {frames.map((frame, index) => (
                    <div key={frame.id} className="p-3 bg-white rounded-lg border border-gray-200">
                      <Text size="md" className="font-medium mb-2">{frame.title}</Text>
                      <Text size="sm" className="text-gray-600 mb-2">
                        You chose: <span className="font-medium">Option {choices[frame.id as 'stayFrame' | 'switchFrame']}</span> ({
                          choices[frame.id as 'stayFrame' | 'switchFrame'] === 'A' 
                          ? frame.options[0] 
                          : frame.options[1]
                        })
                      </Text>
                    </div>
                  ))}
                </div>
                
                <div className="mt-6">
                  <Text size="md" className="font-medium mb-2">Study Results:</Text>
                  
                  <div className="space-y-4">
                    <div>
                      <Text size="sm" className="mb-1">When Option A was presented as the Status Quo:</Text>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Stayed with Option A</span>
                        <span className="font-semibold">{studyResults.stayFrame.stayedWithCurrent}%</span>
                      </div>
                      <div className="h-4 bg-gray-100 rounded-full">
                        <div className="h-4 bg-blue-500 rounded-full" style={{width: `${studyResults.stayFrame.stayedWithCurrent}%`}}></div>
                      </div>
                      
                      <div className="flex justify-between text-sm mb-1 mt-2">
                        <span>Switched to Option B</span>
                        <span className="font-semibold">{studyResults.stayFrame.switchedToNew}%</span>
                      </div>
                      <div className="h-4 bg-gray-100 rounded-full">
                        <div className="h-4 bg-green-500 rounded-full" style={{width: `${studyResults.stayFrame.switchedToNew}%`}}></div>
                      </div>
                    </div>
                    
                    <div>
                      <Text size="sm" className="mb-1">When both options were presented as new choices:</Text>
                      <div className="flex justify-between text-sm mb-1">
                        <span>Chose Option A</span>
                        <span className="font-semibold">{studyResults.switchFrame.stayedWithCurrent}%</span>
                      </div>
                      <div className="h-4 bg-gray-100 rounded-full">
                        <div className="h-4 bg-blue-500 rounded-full" style={{width: `${studyResults.switchFrame.stayedWithCurrent}%`}}></div>
                      </div>
                      
                      <div className="flex justify-between text-sm mb-1 mt-2">
                        <span>Chose Option B</span>
                        <span className="font-semibold">{studyResults.switchFrame.switchedToNew}%</span>
                      </div>
                      <div className="h-4 bg-gray-100 rounded-full">
                        <div className="h-4 bg-green-500 rounded-full" style={{width: `${studyResults.switchFrame.switchedToNew}%`}}></div>
                      </div>
                    </div>
                  </div>
                  
                  <div className="mt-4 p-3 bg-blue-100 rounded-md">
                    <Text size="sm">
                      <strong>Key Finding:</strong> When Option A was presented as the status quo, 60% of participants chose to keep it.
                      When the same option was presented as a new choice (with identical features), only 39% selected it.
                      This 21 percentage point difference demonstrates the powerful influence of status quo bias on our decisions.
                    </Text>
                  </div>
                </div>
              </SimulationStep>

              <ResultBlock 
                title="Understanding Your Results" 
                icon={Brain} 
                variant="highlight"
              >
                <div className="space-y-4">
                  <Text className="text-gray-700">
                    You experienced two different framings of the same investment decision: one where Option A was presented as the status quo (your current investment), and another where both options were presented as new choices.
                  </Text>
                  
                  <Text className="text-gray-700">
                    In the status quo scenario, you chose <span className="font-medium">Option {choices.stayFrame}</span>. In the neutral scenario, you chose <span className="font-medium">Option {choices.switchFrame}</span>.
                  </Text>
                  
                  <Text className="text-gray-700">
                    Status quo bias is our tendency to prefer things to remain the same, even when change might be beneficial. This bias is driven by:
                  </Text>
                  
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    <li>Loss aversion: we feel losses more strongly than equivalent gains</li>
                    <li>Effort avoidance: changing requires energy and decision-making</li>
                    <li>Uncertainty: the current situation feels more predictable</li>
                  </ul>
                </div>
              </ResultBlock>

              <ResultBlock 
                title="In the Original Study" 
                icon={Microscope} 
                variant="info"
              >
                <div className="space-y-4">
                  <Text className="text-gray-700">
                    In Samuelson and Zeckhauser's 1988 study, participants showed a strong preference for maintaining the status quo:
                  </Text>
                  
                  <ul className="list-disc pl-5 space-y-2 text-gray-700">
                    <li>When Option A was framed as the status quo: <span className="font-medium">{studyResults.stayFrame.stayedWithCurrent}%</span> chose to stay with it</li>
                    <li>When both options were new choices: only <span className="font-medium">{studyResults.switchFrame.stayedWithCurrent}%</span> selected Option A</li>
                    <li>This represents a <span className="font-medium">21 percentage point</span> shift due solely to framing</li>
                  </ul>
                  
                  <Text className="text-gray-700">
                    The strength of this bias increased with more alternatives, higher personal importance, and when the status quo had been established longer.
                  </Text>
                </div>
              </ResultBlock>

              <ResultBlock 
                title="About the Study" 
                icon={BookOpen} 
                variant="muted"
              >
                <div className="space-y-4">
                  <Text className="text-gray-700">
                    Based on research by Samuelson & Zeckhauser (1988), "Status Quo Bias in Decision Making," published in the Journal of Risk and Uncertainty. The study demonstrated how framing options as either the status quo or new alternatives significantly influences our choices.
                  </Text>
                  
                  <Text className="text-gray-700">
                    The research extended beyond laboratory settings to examine real-world decision making in areas like healthcare plan selection, retirement savings, and policy implementation.
                  </Text>
                  
                  <Text className="text-sm text-gray-500 italic">
                    This simulation is designed for educational purposes. Individual results may vary.
                  </Text>
                </div>
              </ResultBlock>
              
              <div className="flex justify-center">
                <AccentButton onClick={handleReset}>
                  Try Again
                </AccentButton>
              </div>
            </>
          )}
        </div>
      )}
    </SimulationTemplate>
  );
};

export default StatusQuoBiasSimulation; 